import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import PublishResultCard from "./PublishResultCard";

interface PublishSuccessCardProps {
  courseName?: string;
  /** Display names of the languages that went out, in publish order. */
  languages: string[];
  estimatedTimeInSeconds?: number;
}

/** Whole minutes, rounded up, never zero - "0 min" reads as a bug. */
export const formatMinutes = (seconds: number): number =>
  Math.max(1, Math.ceil(seconds / 60));

export default function PublishSuccessCard(props: PublishSuccessCardProps) {
  const { courseName, languages, estimatedTimeInSeconds } = { ...props };
  const { t } = useTranslation();

  const header = (
    <span className="flex items-center gap-[5px] rounded-[4px] bg-[var(--pi-key-green-faded)] px-[8px] py-[4px] text-[11px] font-semibold text-[var(--pi-key-green)]">
      <span className="flex h-[12px] w-[12px] items-center justify-center text-[12px] leading-none">
        <CheckIcon color="inherit" fontSize="inherit" />
      </span>
      {t("publish_window.published_pill", { defaultValue: "Published" })}
    </span>
  );

  const row = (label: string, value: string) => (
    <div className="flex items-start gap-[12px]">
      <div className="w-[96px] shrink-0 text-[12.5px] leading-[1.5] text-creator-text-soft">
        {label}
      </div>
      <div className="min-w-0 flex-1 text-[12.5px] font-medium leading-[1.5] text-creator-text-sub break-words">
        {value}
      </div>
    </div>
  );

  return (
    <PublishResultCard header={header}>
      <div className="flex flex-col gap-[10px] p-[16px]">
        <div className="text-[10.5px] font-medium text-creator-text-soft">
          {t("publish_window.course_label", { defaultValue: "Course" })}
        </div>
        {courseName && (
          <div className="min-w-0 text-[17px] font-semibold leading-[1.4] text-creator-text break-words">
            {courseName}
          </div>
        )}

        {(languages.length > 0 || estimatedTimeInSeconds !== undefined) && (
          <>
            <div className="h-px w-full bg-creator-border" />
            <div className="flex flex-col gap-[8px]">
              {languages.length > 0 &&
                row(
                  t("publish_window.languages_label", {
                    defaultValue: "Languages",
                  }),
                  languages.join(", ")
                )}
              {estimatedTimeInSeconds !== undefined &&
                row(
                  t("publish_window.duration_label", {
                    defaultValue: "Duration",
                  }),
                  t("publish_window.duration_minutes", {
                    defaultValue: "{{minutes}} min",
                    minutes: formatMinutes(estimatedTimeInSeconds),
                  })
                )}
            </div>
          </>
        )}
      </div>
    </PublishResultCard>
  );
}
